import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Instagram, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import CartDrawer from "./CartDrawer";

const ALL_NAV = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/shop", label: "Shop", requiresShop: true },
  { to: "/journal", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/commissions", label: "Commissions" },
  { to: "/contact", label: "Contact" },
];

const Layout = ({ children }) => {
  const { count, setOpen } = useCart();
  const { settings } = useSettings();
  const shopEnabled = settings.shop_enabled;
  const NAV = ALL_NAV.filter((n) => !n.requiresShop || shopEnabled);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F6F2]/80 border-b border-[#D8D3CC]"
        data-testid="site-header"
      >
        <div className="px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#3B4A3F] flex items-center justify-center">
              <span className="font-serif-display text-xl text-[#3B4A3F]">J</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-xl md:text-2xl text-[#1A1F1B]">
                Jewel Mason
              </div>
              <div className="label-eyebrow text-[10px]">Portfolio and Shop</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10" data-testid="nav-desktop">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                data-testid={`nav-link-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {shopEnabled && (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="relative p-2 text-[#3B4A3F] hover:bg-[#EFECE5] transition-colors"
                data-testid="nav-cart-button"
                aria-label="Open cart"
              >
                <ShoppingBag size={20} strokeWidth={1.4} />
                {count > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#3B4A3F] text-[#F7F6F2] text-[10px] flex items-center justify-center font-medium"
                    data-testid="nav-cart-count"
                  >
                    {count}
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setMobileOpen((s) => !s)}
              className="md:hidden p-2 text-[#3B4A3F]"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-[#D8D3CC] bg-[#F7F6F2]" data-testid="nav-mobile-menu">
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="main-content">
        {children}
      </main>

      <footer className="mt-24 border-t border-[#D8D3CC] bg-[#EFECE5]" data-testid="site-footer">
        <div className="px-6 md:px-12 lg:px-20 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="font-serif-display text-3xl text-[#1A1F1B]">Jewel Mason</div>
            <div className="label-eyebrow text-[10px] mt-1">Portfolio and Shop</div>
            <p className="mt-4 text-sm text-[#4E5C50] max-w-xs leading-relaxed">
              A studio practice in mixed media — acrylic, oil, fabric and the
              quiet rituals of cut-paper collage.
            </p>
          </div>
          <div>
            <div className="label-eyebrow">Explore</div>
            <ul className="mt-4 space-y-2 text-sm text-[#4E5C50]">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-[#3B4A3F] transition-colors">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">Studio</div>
            {settings.contact_instagram ? (
              <a
                href={`https://instagram.com/${settings.contact_instagram}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-[#4E5C50] hover:text-[#3B4A3F] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={16} strokeWidth={1.4} /> @{settings.contact_instagram}
              </a>
            ) : null}
            <p className="mt-6 text-xs text-[#849383]">
              © {new Date().getFullYear()} Jewel Mason. All works © the artist.
            </p>
          </div>
        </div>
      </footer>

      <CartDrawer />
    </div>
  );
};

export default Layout;